import type { ClassLevel, SchoolClass } from '@monecolier/api-client';

/**
 * UX-06 : extrait de `SettingsOverview.tsx` (UX-05) — SEULE source de vérité
 * frontend pour "élèves approuvés / places disponibles". Consommé par
 * `SettingsOverview.tsx` (carte "Élèves") ET `StudentsOverview.tsx` (carte
 * "Élèves inscrits") — jamais un copié-collé de cette logique, sous peine de
 * divergence future entre les deux tableaux de bord (règle métier explicite du
 * ticket UX-06).
 *
 * Règle métier (INCHANGÉE depuis UX-05, voir docs/architecture.md section
 * "UX-05") : `capacity` = somme des `capacity` des classes ACTIVES de l'année en
 * cours, EN EXCLUANT `capacity IS NULL` ; `approved` = décompte d'élèves APPROVED
 * sur CES MÊMES classes (celles qui ont une capacité définie) — un élève déjà
 * approuvé dans une classe SANS capacité configurée n'entre ni dans `approved` ni
 * dans `capacity`, jamais un blocage global. `missingCapacityCount` sert
 * uniquement à déclencher la bannière de priorité (voir
 * `MissingCapacityBanner.tsx`).
 */
export interface EnrolledStudentsSummary {
  approved: number;
  capacity: number;
  missingCapacityCount: number;
}

export function computeEnrolledStudentsSummary(
  classesByLevel: Partial<Record<ClassLevel, SchoolClass[]>>,
): EnrolledStudentsSummary {
  const activeYearClasses = Object.values(classesByLevel)
    .flat()
    .filter((schoolClass): schoolClass is SchoolClass => !!schoolClass && schoolClass.isActiveYear);
  const classesWithCapacity = activeYearClasses.filter((schoolClass) => schoolClass.capacity !== null);

  return {
    approved: classesWithCapacity.reduce((sum, schoolClass) => sum + schoolClass.studentCount, 0),
    capacity: classesWithCapacity.reduce((sum, schoolClass) => sum + (schoolClass.capacity ?? 0), 0),
    missingCapacityCount: activeYearClasses.length - classesWithCapacity.length,
  };
}
